import * as React from 'react';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { CheckmarkCircleFilled, ErrorCircleFilled, InfoFilled, WarningFilled } from '@fluentui/react-icons';

type MessageType = 'success' | 'danger' | 'warning' | 'info';
interface Props {
  children: React.ReactNode;
  className?: string;
  type: MessageType;
}

const typeToIcon: Record<MessageType, typeof ErrorCircleFilled> = {
  danger: ErrorCircleFilled,
  info: InfoFilled,
  success: CheckmarkCircleFilled,
  warning: WarningFilled,
};

const typeToColor: Record<MessageType, string> = {
  danger: tokens.colorPaletteRedForeground1,
  info: tokens.colorNeutralForeground1,
  success: tokens.colorPaletteGreenForeground1,
  warning: tokens.colorPaletteYellowForeground2,
};

export function MessageBar({ children, className, type }: Props) {
  const classes = useStyles();

  const Icon = typeToIcon[type];

  return (
    <div
      data-testid="messagebar"
      className={mergeClasses(classes.root, classes[`root-${type}`], className)}
      role="region"
    >
      <span className={classes.icon}>
        {/* nb Span hack to force width on Fluent Icon; Component seems to ignore fontSize in some cases. */}
        <Icon fontSize={24} color={typeToColor[type]} />
      </span>
      <div role={typeToRole[type]} aria-live={typeToLive[type]} className={classes.content}>
        {children}
      </div>
    </div>
  );
}

const typeToRole: Record<Props['type'], string> = {
  danger: 'alert',
  info: 'status',
  success: 'status',
  warning: 'status',
};

const typeToLive: Record<Props['type'], 'assertive' | 'off' | 'polite' | undefined> = {
  danger: 'assertive',
  info: 'polite',
  success: 'polite',
  warning: 'polite',
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingHorizontalM, tokens.spacingVerticalM),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
  },
  /* eslint-disable @foundershub/no-unused-fluent-styles */
  'root-danger': {
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorPaletteRedBorder1),
  },
  'root-info': {
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
  },
  'root-success': {
    backgroundColor: tokens.colorPaletteGreenBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorPaletteGreenBorder1),
  },
  'root-warning': {
    backgroundColor: tokens.colorPaletteYellowBackground1,
    ...shorthands.border(tokens.strokeWidthThin, 'solid', tokens.colorPaletteYellowBorder2),
  },
  /* eslint-enable @foundershub/no-unused-fluent-styles */
  icon: {
    width: '24px',
    height: '24px',
  },

  content: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingHorizontalS),
    flexBasis: 'auto',
    flexGrow: 1,
    flexShrink: 1,
  },
});
